import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import { Contact, ContactParticipant } from '../../../api/app-types/contact.app';
import { DOCUMENT_CATEGORY, DocumentEntry, DocumentFile, DocumentNavItem } from '../../../api/app-types/document.app';
import { PERSON_TYPE, personTypeTranslationKeys } from '../../../api/app-types/search.app';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrDocumentItem } from '../../../core/components/document-item/document-item';
import { LvrHeading } from '../../../core/components/heading/heading';
import { CarouselElement, NavCarousel } from '../../../core/components/nav-carousel/nav-carousel';
import { SendEmailButton } from '../../../core/components/send-email-button/send-email-button';
import { formatDateToDisplay } from '../../../shared/helpers/date.func';
import { calculateRemainingEditDays } from '../../../shared/helpers/remaining-edit-days.func';
import { LvrPermissionDirective } from '../../../shared/permissions/permission.directive';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { PERMISSION_RESOURCE } from '../../../shared/permissions/permissions.types';

@Component({
	selector: 'lvr-contact-details',
	imports: [NavCarousel, LvrHeading, TranslocoPipe, DatePipe, SendEmailButton, LvrDocumentItem, LvrPermissionDirective],
	templateUrl: './contact-details.html',
	styleUrl: './contact-details.scss',
})
export class ContactDetails extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	private readonly contactResponse = this.resolveDataAsSignal<ApiResponse<Contact, Metadata>>('contact')!;
	public readonly contact = computed(() => this.contactResponse()?.data);
	public readonly metadata = computed(() => this.contactResponse()?.metadata);
	private readonly editableSet = computed(() => new Set(this.metadata()?.editOnlyFields));
	public readonly isFrozen = computed(() => this.editableSet().size > 0);
	public readonly contacts = this.resolveDataAsSignal<Contact[]>('contacts');
	public readonly clientId = this.resolveRouteParamAsSignal('id');
	public readonly canReadContactsRestricted = hasPermission(this.session.permissions, 'Contacts.Read.Restricted');
	public readonly readPermissions: PERMISSION_RESOURCE[] = [
		'Contacts.Read',
		'Contacts.Read.PhaseOneOnly',
		'Contacts.Read.Restricted',
	];

	public readonly carouselElements = computed(() =>
		(this.contacts() ?? []).map((c) => ({
			id: c.id?.toString() ?? '',
			label: `${c.keyword}`,
		})),
	);

	public readonly currentIndex = computed(() => {
		const id = this.contact()?.id.toString();
		return this.carouselElements().findIndex((e) => e.id === id);
	});

	public readonly backLink = computed(() =>
		this.router.createUrlTree(['../../../'], { relativeTo: this.route }).toString(),
	);

	public readonly isEditDisabled = computed(() => this.remainingEditDays() < 0);
	public readonly canEditContacts = hasPermission(this.session.permissions, [
		'Contacts.Edit',
		'Contacts.Edit.PhaseOneOnly',
	]);

	public readonly canEditRestricted = computed(() => {
		if (!this.canEditContacts()) return false;
		if (this.contact()?.confidential) return this.canReadContactsRestricted();
		return true;
	});

	public readonly canReadContactDocuments = hasPermission(this.session.permissions, 'Documents.Contact.Read');

	public readonly editDisabledReason = computed(() => {
		if (this.isFrozen()) return 'contact.details.edit_disabled.used_in_report';
		if (this.isEditDisabled()) return 'contact.details.edit_disabled.too_old';
		return '';
	});

	public readonly disableEditTranslationParams = computed(() => {
		if (!this.isEditDisabled()) return;

		const limitInDays = this.env.formConfiguration.contact.updateCreatePeriodAsDays;
		return { days: limitInDays };
	});

	public readonly isClientParticipant = computed(
		() =>
			this.contact()?.participants?.some(
				(v) => Number(v.item.id) === Number(this.clientId()) && v.item.personTypeId === PERSON_TYPE.CLIENT,
			) ?? false,
	);

	public mapPersonTypeToKey(pt: PERSON_TYPE) {
		return personTypeTranslationKeys[pt] || 'unknown';
	}

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id], { relativeTo: this.route });
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	public onFileClick(file: DocumentFile) {
		if (!this.canReadContactDocuments()) return;
		const contact = this.contact();
		if (!contact) return;

		const navItems = this.buildContactNavItems(contact);
		this.router.navigate(['/desk/client', this.clientId(), 'documents', file.id], {
			state: { navItems: navItems, returnUrl: this.router.url },
		});
	}

	public buildDocumentEntryFromContact(doc: DocumentFile) {
		const contact = this.contact();
		if (!contact) return;

		const createdAt = contact.contactedAt ?? new Date();
		const documentEntry: DocumentEntry = {
			id: contact.id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.CONTACT,
			createdAt: createdAt,
			externalFileReference: contact.documents ?? [],
			isReport: false,
			isFrozen: this.isFrozen(),
		};
		return documentEntry;
	}

	private buildContactNavItems(contact: Contact): DocumentNavItem[] {
		const files = contact.documents ?? [];
		const createdAt = contact.contactedAt ?? new Date();
		const entryName = this.transloco.translate('contact.details.title', { date: formatDateToDisplay(createdAt) });

		return files.map((file) => ({
			file,
			entryId: contact.id,
			entryName,
			category: DOCUMENT_CATEGORY.CONTACT,
			createdAt,
			isReport: false,
			isFrozen: this.isFrozen(),
		}));
	}

	public trackItem(item: ContactParticipant) {
		const trackBy = item.item.id + '-' + item.personTypeId;
		return trackBy;
	}

	public isContactTypeAnEmail(contact?: Contact) {
		const remark = contact?.contactType?.remark?.toLowerCase();
		return remark == 'email' || remark == 'e-mail';
	}

	/**
	 * Computes how many days are left before the contact's edit period expires.
	 * Returns negative values if the deadline has already passed.
	 */
	private readonly remainingEditDays = computed(() => {
		const contactedAt = this.contact()?.contactedAt;
		if (!contactedAt) return -1;

		return calculateRemainingEditDays(contactedAt, this.env.formConfiguration.contact.updateCreatePeriodAsDays);
	});
}
